/**
 * The "Multiple Dates" / "Delivery Date" modal where a customer sets one requested (future)
 * delivery date for one or more lines: it lists when each line is available to ship, and offers a
 * date picker limited to the clamped range of the selected lines' dates. Apply hands the chosen
 * date to the caller; "Remove Future Date" hands it `undefined`. The modal should offer only
 * dates every line allows; today it offers the clamped range, a known defect described at
 * `FutureDatesHelpers.tsx` → `getFilteredDeliveryTimes`.
 *
 * Position: rendered by the cart (`ShoppingCart/ShoppingCart.tsx`), quote details
 * (`QuoteDetails/QuoteDetails.tsx`), Easy Quote (`EasyQuote/EasyQuote.tsx`) and express checkout
 * (`Checkout/ExpressCheckout/ExpressCheckout.tsx`, and for quote checkout through
 * `QuoteCheckoutFutureDatesModal/QuoteCheckoutFutureDatesModal.tsx`). Opened from a row's
 * `FutureDatesTrigger.tsx` or from a bulk "select delivery date" action. This component owns the
 * date selection and the error state; `FutureDatesDesktop.tsx` (lg and up) and
 * `FutureDatesMobile.tsx` (below lg) are layouts only. The common-date arithmetic is in
 * `FutureDatesHelpers.tsx`.
 *
 * Rules a change must keep:
 * - Callers give each row an `itemNumber` that matches the delivery-times response (a cart
 *   line's 1-based position) and pass copies of their rows: `handleChange` writes the picked
 *   date into the row object it receives.
 * - The handler, not this modal, persists the date (cart API for a signed-in cart, local state
 *   otherwise; see `ShoppingCart/Context/ShoppingCartContext.tsx` → `handleOnDeliveryDatesChange`).
 */
import React, { useEffect, useRef, useState } from "react";
import { FutureDatesDesktop } from "./FutureDatesDesktop";
import { Media } from "@ryerson/frontend.next";
import { FutureDatesMobile } from "./FutureDatesMobile";
import { DraftQuoteLineItem, ShoppingCartDataType } from "@ryerson/frontend.common";
import { cloneDeep } from "lodash";
import { dateToString } from "@components/Helper/common";
import { useEcommerce } from "@ryerson/frontend.e-commerce";
import { IDeliveryTimesResponse } from "@ryerson/common.models";
import { getDeliveryDateAndValidDateRange } from "@components/Helper/DeliveryDateAndMinMaxRange";
import {
	getCommonLeadTime,
	getCommonEarliest,
	getFilteredDeliveryTimes,
} from "./FutureDatesHelpers";
import { FutureDatesContent } from "./FutureDatesContent";
import { useApplication } from "@ryerson/frontend.application";

/**
 * Receives the date applied in the modal for all `rows`, or `undefined` when the customer
 * removes the future date. Implemented by the cart, express-checkout and quote-details screens.
 */
export type FutureDatesHandler = (
	value: Date | undefined,
	rows: ((ShoppingCartDataType & { itemNumber: number }) | DraftQuoteLineItem)[]
) => void;

/** Props of {@link FutureDatesModal}. */
export type FutureDatesModalProps = {
	handleOnDeliveryDatesChange: FutureDatesHandler;
	/** Disables the date picker while the caller is re-fetching prices or delivery times. */
	isFetching: boolean;
	modalOpen: boolean;
	onClose: () => void;
	/**
	 * The lines the date applies to. `canSelectFutureDeliveryDateForTBDItems` marks a customer-part
	 * line whose delivery date is TBD but which may still request a date (the sales rep confirms
	 * it later); the modal then shows the sales-rep message, and titles itself "Delivery Date"
	 * instead of "Multiple Dates" when the line has a single lead-time day. Only `rows[0]`
	 * decides the title, the message and the picker's starting date.
	 */
	rows: (
		| (ShoppingCartDataType & {
				itemNumber: number;
				isFutureDeliveryDate?: boolean;
				canSelectFutureDeliveryDateForTBDItems: boolean;
		  })
		| (DraftQuoteLineItem & {
				isFutureDeliveryDate?: boolean;
				canSelectFutureDeliveryDateForTBDItems: boolean;
		  })
	)[];
	setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
	/** The whole delivery-times response; the modal keeps only the entries for `rows`. */
	productsDeliveryTimes: IDeliveryTimesResponse[];
	/** Replaces the computed title; quote checkout passes "Delivery Date". */
	titleOverride?: string;
};

/**
 * Props the modal passes to its two layouts, `FutureDatesDesktop.tsx` and `FutureDatesMobile.tsx`.
 * `productsDeliveryTimes` here is already narrowed and clamped by `getFilteredDeliveryTimes`, and
 * `filteredDeliveryTimes` is the slowest of those entries (`getCommonEarliest`), which feeds the
 * date picker.
 */
export type FutureDatesType = {
	handleApply: () => void;
	isFetching: boolean;
	modalOpen: boolean;
	onClose: () => void;
	rows: (
		| (ShoppingCartDataType & {
				itemNumber: number;
				canSelectFutureDeliveryDateForTBDItems: boolean;
		  })
		| (DraftQuoteLineItem & { canSelectFutureDeliveryDateForTBDItems: boolean })
	)[];
	setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
	productsDeliveryTimes: IDeliveryTimesResponse[];
	handleChange: (date: Date, rowData: ShoppingCartDataType, rowIndex: number) => void;
	showError: boolean;
	errorMessage: string;
	filteredDeliveryTimes?: IDeliveryTimesResponse;
	handleRemove: () => void;
	popperDisplayedState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
	triggerRef: React.MutableRefObject<HTMLDivElement | null | undefined>;
	titleOverride?: string;
};

/** A cart line as a modal row: with its 1-based `itemNumber` and the customer-part TBD flag. */
export interface UpdatedShoppingCartDataType extends ShoppingCartDataType {
	itemNumber: number;
	canSelectFutureDeliveryDateForTBDItems: boolean;
}

/**
 * Shared future-delivery-date modal (see the file header). Shows the "no overlap" error when the
 * delivery-times response is non-empty but has no entry for the selected rows, or when there are
 * no rows. Lines whose dates don't overlap produce no error: the clamped list is empty, and Apply
 * still sends the picked date or the default from `getSelectedDate`. Shows "Please select a
 * date." when Apply finds neither a picked date nor a default. Apply and Remove both call
 * `onClose` and close the modal.
 */
export const FutureDatesModal: React.FC<FutureDatesModalProps> = ({
	rows,
	handleOnDeliveryDatesChange,
	isFetching,
	modalOpen,
	setModalOpen,
	onClose,
	productsDeliveryTimes,
	titleOverride,
}) => {
	const { flexibleDelivery } = useEcommerce();
	const {
		localization: { language },
	} = useApplication();

	/**
	 * The delivery-times entries for `rows`,
	 * with allowed delivery dates clamped to a common range (not an intersection).
	 * The modal should offer only dates every line allows; see `getFilteredDeliveryTimes` for the
	 * known defect.
	 */
	const filteredDeliveryTimes = getFilteredDeliveryTimes(
		cloneDeep(productsDeliveryTimes),
		cloneDeep(rows)
	);

	const [noDateSelectedErr, setNoDateSelectedErr] = useState<boolean>(false);
	const [selectedDate, setSelectedDate] = useState<Date | undefined>();
	const popperDisplayedState = React.useState<boolean>(false);
	const triggerRef = useRef<HTMLDivElement | null>(null);

	const commonEarliest = getCommonEarliest(filteredDeliveryTimes);
	/**
	 * If the response has no entry for the selected rows, or there are no rows,
	 * display the `no dates` error. An empty clamped date list does not set it.
	 */
	const noDatesError =
		!productsDeliveryTimes ||
		!filteredDeliveryTimes ||
		(filteredDeliveryTimes.length === 0 && productsDeliveryTimes.length > 0) ||
		rows.length === 0;
	const content = FutureDatesContent[language || "en"];
	const errorMessage = noDatesError ? content.noOverlappingDates : content.pleaseSelectAlert;

	/**
	 * The date the picker opens on, and the date Apply uses when the customer picked none: the
	 * common lead-time day's `deliveryDate`, moved by flexible delivery, or `rows[0]`'s requested
	 * date when that lies inside the common range. `undefined` when there is none.
	 */
	const getSelectedDate = () => {
		if (!filteredDeliveryTimes.length) {
			return undefined;
		}
		const deliveryDate = getCommonLeadTime(filteredDeliveryTimes);

		let { startDate } = getDeliveryDateAndValidDateRange(
			true,
			flexibleDelivery,
			deliveryDate,
			rows[0]?.requestedDeliveryDate,
			true //useAllowedDeliveryDates
		);
		return startDate !== null ? startDate : undefined;
	};

	useEffect(() => {
		setSelectedDate(cloneDeep(getSelectedDate()));
	}, [rows]);

	function handleChange(date: Date, row: ShoppingCartDataType, rowIndex: number) {
		const formattedDate = dateToString(date);

		/**
		 * I’m updating this synchronously here because this is a local copy,
		 * and the rows only get updated when the modal opens or closes
		 */
		row.requestedDeliveryDate = formattedDate;
		setSelectedDate(cloneDeep(date));
	}

	const handleApply = () => {
		if (selectedDate === undefined) {
			let date = getSelectedDate();
			if (date) {
				handleOnDeliveryDatesChange(new Date(date), rows);
				onClose();
				setModalOpen(false);
			} else {
				setNoDateSelectedErr(true);
			}
		} else {
			handleOnDeliveryDatesChange(new Date(selectedDate), rows);
			onClose();
			setModalOpen(false);
		}
	};
	const handleRemoveDates = () => {
		handleOnDeliveryDatesChange(undefined, rows);
		onClose();
		setModalOpen(false);
	};

	return (
		<>
			<Media lessThan="lg">
				<FutureDatesMobile
					handleApply={handleApply}
					handleChange={handleChange}
					isFetching={isFetching}
					modalOpen={modalOpen}
					onClose={() => {
						setNoDateSelectedErr(false);
						onClose();
					}}
					productsDeliveryTimes={filteredDeliveryTimes}
					rows={rows}
					setModalOpen={setModalOpen}
					showError={noDatesError || noDateSelectedErr}
					errorMessage={errorMessage}
					filteredDeliveryTimes={commonEarliest}
					handleRemove={handleRemoveDates}
					popperDisplayedState={popperDisplayedState}
					triggerRef={triggerRef}
					titleOverride={titleOverride}
				/>
			</Media>
			<Media greaterThanOrEqual="lg">
				<FutureDatesDesktop
					handleApply={handleApply}
					handleChange={handleChange}
					isFetching={isFetching}
					modalOpen={modalOpen}
					onClose={() => {
						setNoDateSelectedErr(false);
						onClose();
					}}
					productsDeliveryTimes={filteredDeliveryTimes}
					rows={rows}
					setModalOpen={setModalOpen}
					showError={noDatesError || noDateSelectedErr}
					errorMessage={errorMessage}
					filteredDeliveryTimes={commonEarliest}
					handleRemove={handleRemoveDates}
					popperDisplayedState={popperDisplayedState}
					triggerRef={triggerRef}
					titleOverride={titleOverride}
				/>
			</Media>
		</>
	);
};
